/**
 * The exercise menu: a title screen that asks what is being trained today.
 *
 * It opens over the live camera rather than before it, which is deliberate. The
 * athlete has to frame the shot — side-on, head to floor, feet in — and the
 * menu's own hint says so, so it is the one moment where seeing yourself in the
 * frame while reading the list is useful. The panel is translucent over the
 * video for exactly that reason.
 *
 * A console's menu, so the arrow keys and Enter work as well as a tap: the phone
 * is on a tripod but this screen is also the one used from a laptop while
 * reviewing a recording.
 *
 * It doubles as the character screen: each drill carries a level and a bar
 * towards the next one, earned in clean reps and kept on the device (see
 * metrics/progress.js). That is a motivator rather than a measurement, and the
 * XP is clean reps for a reason — a bar that filled with any rep at all would
 * pay the athlete to do bad ones, which is the one thing a rep counter makes
 * easy and the one thing this app must not reward.
 *
 * Calibration is said on the card, not buried in a doc, and in the athlete's
 * words: a drill reads "ready" or "experimental". An experimental drill counts
 * reps honestly and scores them against numbers nobody measured, and the
 * difference between that and a ready one is not something to leave the athlete
 * to infer from a score that looks identical either way.
 */

/** Cells in the XP bar. Twenty is what the loading bar and the rep bar use. */
const XP_CELLS = 20;

export class ExerciseMenu {
  /**
   * @param exercises The registry, in menu order.
   * @param handlers.onPick Called with the chosen exercise.
   */
  constructor(exercises, { onPick, progress = null }) {
    this.exercises = exercises;
    this.onPick = onPick;
    this.progress = progress;
    this.panel = document.getElementById("menu");
    this.list = document.getElementById("menu-list");
    this.buttons = [];
    /** Per exercise id, the nodes `refresh()` writes the level into. */
    this.levels = new Map();

    exercises.forEach((exercise) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "control menu-item";
      button.dataset.exercise = exercise.id;

      const head = document.createElement("span");
      head.className = "menu-head";
      const name = document.createElement("span");
      name.className = "menu-name";
      name.textContent = exercise.name;
      head.appendChild(name);
      const level = document.createElement("span");
      level.className = "menu-level";
      head.appendChild(level);
      button.appendChild(head);

      const blurb = document.createElement("span");
      blurb.className = "menu-blurb";
      blurb.textContent = exercise.blurb;
      button.appendChild(blurb);

      const tag = document.createElement("span");
      tag.className = exercise.calibrated
        ? "menu-tag menu-tag-ready"
        : "menu-tag menu-tag-raw";
      // "Ready" and "Experimental" rather than "calibrated" and "not
      // calibrated": the athlete is picking a drill, not auditing the
      // thresholds, and the word that matters to him is whether this one is
      // finished. The field behind it is still `calibrated` and still means
      // the protocol in `fixtures/README.md`.
      tag.textContent = exercise.calibrated
        ? `${exercise.view} view · ready`
        : `${exercise.view} view · experimental`;
      button.appendChild(tag);

      // The bar is cells rather than a fill, like the loading bar: a block
      // appearing reads as progress at a glance where a sliver growing does not.
      const track = document.createElement("span");
      track.className = "menu-xp";
      const cells = [];
      for (let i = 0; i < XP_CELLS; i += 1) {
        const cell = document.createElement("span");
        track.appendChild(cell);
        cells.push(cell);
      }
      button.appendChild(track);

      const earned = document.createElement("span");
      earned.className = "menu-xp-label";
      button.appendChild(earned);

      this.levels.set(exercise.id, { level, cells, earned, track });

      button.addEventListener("click", () => this.pick(exercise));
      this.list.appendChild(button);
      this.buttons.push(button);
    });

    this.onKey = (event) => this.handleKey(event);
    window.addEventListener("keydown", this.onKey);
  }

  get visible() {
    return !this.panel.hidden;
  }

  /**
   * Writes the levels in. Called on every show, because a set banked since the
   * last one is exactly what the athlete came back to this screen to see.
   */
  refresh() {
    if (this.progress == null) {
      return;
    }
    for (const exercise of this.exercises) {
      const nodes = this.levels.get(exercise.id);
      const { level, into, need } = this.progress.levelOf(exercise.id);
      nodes.level.textContent = `Lv ${level}`;
      nodes.earned.textContent = `${into}/${need} clean reps to Lv ${level + 1}`;
      const filled = Math.round((into / need) * XP_CELLS);
      nodes.cells.forEach((cell, i) => {
        cell.className = i < filled ? "on" : "";
      });
      nodes.track.setAttribute(
        "aria-label",
        `Level ${level}, ${into} of ${need} clean reps to level ${level + 1}`
      );
    }
  }

  show() {
    this.refresh();
    this.panel.hidden = false;
    if (this.buttons.length > 0) {
      this.buttons[0].focus();
    }
  }

  hide() {
    this.panel.hidden = true;
  }

  pick(exercise) {
    this.hide();
    this.onPick(exercise);
  }

  handleKey(event) {
    if (!this.visible || event.repeat) {
      return;
    }
    const index = this.buttons.indexOf(document.activeElement);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next =
        (Math.max(0, index) + step + this.buttons.length) % this.buttons.length;
      this.buttons[next].focus();
    } else if (event.code === "Space" && index >= 0) {
      // The drill bar listens for Space too, and until a drill is chosen there
      // is nothing for it to start.
      event.preventDefault();
      this.buttons[index].click();
    }
  }
}
